import { TagTypes } from "constants/tags";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  Tag,
  TagActionTemplate,
  TagActionTemplateCreate,
  TagActionTemplateFilter,
  TagActionTemplateUpdate,
  TagCreate,
  TagFilter,
  TagInput,
  TagsCountResponse,
  TagsResponse
} from "./types/tags.type";

const GET_TAG_QUERY = "{id,name,tooltip,color,type}";

const tagsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getTags: builder.query<TagsResponse, TagFilter>({
      query: (filter) =>
        `global-tags/?${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["Tags"]
    }),
    // ENG-1305: total count for the settings tags admin grid. The list endpoint
    // stays flat (NoCountFlatPagination) so the 16 other callers pay zero count
    // cost; only the admin grid pulls this. Reuses TagFilter so the count
    // matches the active list filters (type, search, is_active, verified).
    getTagsCount: builder.query<TagsCountResponse, TagFilter>({
      query: (filter) =>
        `global-tags/count/?${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["Tags"]
    }),
    getTag: builder.query<Tag, number>({
      query: (tagId) => `/global-tags/${tagId}/?query=${GET_TAG_QUERY}`,
      providesTags: (result) => [
        {
          type: "Tags" as const,
          id: result?.id
        }
      ]
    }),
    createTag: builder.mutation<Tag, TagCreate>({
      query: (newTag) => ({
        url: "global-tags/",
        method: "POST",
        body: newTag
      }),
      invalidatesTags: ["Tags"]
    }),
    updateTag: builder.mutation<Tag, TagInput>({
      query: ({ id, ...updatedTag }) => ({
        url: `global-tags/${id}/`,
        method: "PUT",
        body: updatedTag
      }),
      invalidatesTags: ["Tags"]
    }),
    deleteTag: builder.mutation<Tag, { id: number }>({
      query: ({ id }) => ({
        url: `global-tags/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Tags"]
    }),
    getPatientTags: builder.query<TagsResponse, TagFilter | void>({
      query: (filter = {}) =>
        `global-tags/?${formatValidQueryParamUrlCommaSeparated({
          ...filter,
          type: TagTypes.PATIENT
        })}`,
      providesTags: ["PatientTags"]
    }),
    getTagActionTemplates: builder.query<
      TagActionTemplate[],
      TagActionTemplateFilter
    >({
      query: (filter = {}) =>
        `tag-action-templates/?${formatValidQueryParamUrlCommaSeparated(
          filter
        )}`,
      providesTags: ["TagActionTemplates"]
    }),
    createTagActionTemplate: builder.mutation<
      TagActionTemplate,
      TagActionTemplateCreate
    >({
      query: (newTemplate) => ({
        url: "tag-action-templates/",
        method: "POST",
        body: newTemplate
      }),
      invalidatesTags: ["TagActionTemplates", "StaffTasks"]
    }),
    getTagActionTemplate: builder.query<TagActionTemplate, number>({
      query: (id) => `tag-action-templates/${id}/`,
      providesTags: (result) => [
        {
          type: "TagActionTemplates" as const,
          id: result?.id
        }
      ]
    }),
    updateTagActionTemplate: builder.mutation<
      TagActionTemplate,
      TagActionTemplateUpdate
    >({
      query: ({ id, ...updatedTemplate }) => ({
        url: `tag-action-templates/${id}/`,
        method: "PUT",
        body: updatedTemplate
      }),
      invalidatesTags: ["TagActionTemplates", "StaffTasks"]
    }),
    deleteTagActionTemplate: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `tag-action-templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["TagActionTemplates", "StaffTasks"]
    })
  })
});

export const {
  useGetTagsQuery,
  useLazyGetTagsQuery,
  useGetTagsCountQuery,
  useGetTagQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetPatientTagsQuery,
  useGetTagActionTemplatesQuery,
  useGetTagActionTemplateMutation,
  useCreateTagActionTemplateMutation,
  useUpdateTagActionTemplateMutation,
  useDeleteTagActionTemplateMutation
} = tagsApi;
